'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';

type PlatformId =
  | 'amazon'
  | 'shopify'
  | 'etsy'
  | 'ebay'
  | 'walmart-marketplace'
  | 'facebook-marketplace'
  | 'tiktok-shop';

type CheckStatus = 'pass' | 'warn' | 'fail';
type OverallStatus = 'pass' | 'needs-fixing' | 'not-suitable';
type QueueCheckState = 'idle' | 'checking' | 'done';

type PlatformProfile = {
  id: PlatformId;
  label: string;
  summary: string;
  minLongSide: number;
  minShortSide: number;
  idealLongSide: number;
  passRatioRange: [number, number];
  warnRatioRange: [number, number];
  maxSuggestedSizeMb: number;
  preferredFormats: Array<'jpeg' | 'png' | 'webp'>;
  prefersLightBackground: boolean;
  disallowTransparency: boolean;
  safeInset: number;
  warning: string;
};

type LoadedItem = {
  id: string;
  file: File;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
  previewUrl: string;
  checkState: QueueCheckState;
  result: ComplianceResult | null;
  error: string | null;
};

type ComplianceCheck = {
  id: 'dimensions' | 'aspect' | 'file-size' | 'format' | 'transparency' | 'background' | 'framing';
  label: string;
  status: CheckStatus;
  detail: string;
  fixHref: string | null;
  fixLabel: string | null;
};

type ComplianceResult = {
  overall: OverallStatus;
  title: string;
  summary: string;
  checks: ComplianceCheck[];
  platformWarning: string;
  actions: FixAction[];
};

type FixAction = {
  href: string;
  label: string;
  title: string;
};

type Drawable = {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
};

type ImageAnalysis = {
  transparentRatio: number;
  borderLuma: number;
  borderVariance: number;
  subjectBounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } | null;
};

const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp']);

const maxBatchFiles = 24;
const maxFileSize = 15 * 1024 * 1024;

const fixToolMap: Record<'resize' | 'background' | 'batch' | 'metadata', FixAction> = {
  resize: {
    href: '/tools/resizing-cropping',
    label: 'Fix with resizing',
    title: 'Resizing and cropping',
  },
  background: {
    href: '/tools/product-photo-background-cleaner',
    label: 'Fix background',
    title: 'Marketplace background & framing studio',
  },
  batch: {
    href: '/tools/batch-listing-asset-optimizer',
    label: 'Optimize batch',
    title: 'Batch listing asset optimizer',
  },
  metadata: {
    href: '/tools/metadata-removal',
    label: 'Strip metadata',
    title: 'Metadata removal',
  },
};

const platformProfiles: PlatformProfile[] = [
  {
    id: 'amazon',
    label: 'Amazon',
    summary: 'Square, centered, light-background listing images.',
    minLongSide: 1000,
    minShortSide: 1000,
    idealLongSide: 1600,
    passRatioRange: [0.92, 1.08],
    warnRatioRange: [0.8, 1.2],
    maxSuggestedSizeMb: 10,
    preferredFormats: ['jpeg', 'png', 'webp'],
    prefersLightBackground: true,
    disallowTransparency: true,
    safeInset: 0.08,
    warning:
      'Practical readiness check only. Clean white backgrounds and centered framing usually read best for Amazon-style listing use cases.',
  },
  {
    id: 'shopify',
    label: 'Shopify',
    summary: 'Consistent storefront-ready product images.',
    minLongSide: 1200,
    minShortSide: 1200,
    idealLongSide: 2048,
    passRatioRange: [0.8, 1.2],
    warnRatioRange: [0.65, 1.6],
    maxSuggestedSizeMb: 15,
    preferredFormats: ['webp', 'jpeg', 'png'],
    prefersLightBackground: false,
    disallowTransparency: false,
    safeInset: 0.06,
    warning:
      'Practical readiness check only. Shopify workflows usually benefit from consistent aspect and lighter storefront-ready files across the whole product set.',
  },
  {
    id: 'etsy',
    label: 'Etsy',
    summary: 'Portrait-friendly handmade listing presentation.',
    minLongSide: 1500,
    minShortSide: 1200,
    idealLongSide: 2000,
    passRatioRange: [0.72, 0.92],
    warnRatioRange: [0.65, 1.12],
    maxSuggestedSizeMb: 12,
    preferredFormats: ['jpeg', 'png', 'webp'],
    prefersLightBackground: false,
    disallowTransparency: false,
    safeInset: 0.08,
    warning:
      'Practical readiness check only. Etsy-style listings often benefit from taller product framing and a little more breathing room around the subject.',
  },
  {
    id: 'ebay',
    label: 'eBay',
    summary: 'Straightforward marketplace framing with lighter files.',
    minLongSide: 1200,
    minShortSide: 1000,
    idealLongSide: 1600,
    passRatioRange: [0.85, 1.15],
    warnRatioRange: [0.72, 1.4],
    maxSuggestedSizeMb: 10,
    preferredFormats: ['jpeg', 'webp', 'png'],
    prefersLightBackground: false,
    disallowTransparency: false,
    safeInset: 0.08,
    warning:
      'Practical readiness check only. eBay-style listing workflows usually reward lighter file sizes and a clear product frame across the photo set.',
  },
  {
    id: 'walmart-marketplace',
    label: 'Walmart Marketplace',
    summary: 'Square catalog framing with a clean light background.',
    minLongSide: 1500,
    minShortSide: 1200,
    idealLongSide: 1800,
    passRatioRange: [0.92, 1.08],
    warnRatioRange: [0.82, 1.18],
    maxSuggestedSizeMb: 10,
    preferredFormats: ['jpeg', 'png', 'webp'],
    prefersLightBackground: true,
    disallowTransparency: true,
    safeInset: 0.08,
    warning:
      'Practical readiness check only. Clean square catalog framing and light backgrounds usually travel better for Walmart Marketplace-style listing workflows.',
  },
  {
    id: 'facebook-marketplace',
    label: 'Facebook Marketplace',
    summary: 'Square, lighter images for mobile-first listings.',
    minLongSide: 1200,
    minShortSide: 1000,
    idealLongSide: 1600,
    passRatioRange: [0.9, 1.1],
    warnRatioRange: [0.8, 1.25],
    maxSuggestedSizeMb: 8,
    preferredFormats: ['jpeg', 'webp', 'png'],
    prefersLightBackground: false,
    disallowTransparency: false,
    safeInset: 0.08,
    warning:
      'Practical readiness check only. Mobile-first marketplace surfaces usually reward square, lighter, more centered product images.',
  },
  {
    id: 'tiktok-shop',
    label: 'TikTok Shop',
    summary: 'Mobile-first square product images with lighter files.',
    minLongSide: 1200,
    minShortSide: 1000,
    idealLongSide: 1600,
    passRatioRange: [0.92, 1.08],
    warnRatioRange: [0.8, 1.18],
    maxSuggestedSizeMb: 8,
    preferredFormats: ['webp', 'jpeg', 'png'],
    prefersLightBackground: false,
    disallowTransparency: false,
    safeInset: 0.08,
    warning:
      'Practical readiness check only. TikTok Shop-style product cards usually work best with mobile-first framing and smaller, faster-loading assets.',
  },
];

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Image';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getInputFormat(fileType: string): 'jpeg' | 'png' | 'webp' | 'gif' | 'bmp' | 'unknown' {
  if (fileType === 'image/jpeg') {
    return 'jpeg';
  }
  if (fileType === 'image/png') {
    return 'png';
  }
  if (fileType === 'image/webp') {
    return 'webp';
  }
  if (fileType === 'image/gif') {
    return 'gif';
  }
  if (fileType === 'image/bmp') {
    return 'bmp';
  }
  return 'unknown';
}

function getOverallStatusLabel(status: OverallStatus) {
  switch (status) {
    case 'pass':
      return 'Pass';
    case 'needs-fixing':
      return 'Needs fixing';
    default:
      return 'Not suitable';
  }
}

function getQueueStatusLabel(item: LoadedItem) {
  if (item.checkState === 'checking') {
    return 'Checking';
  }

  if (!item.result) {
    return 'Ready to check';
  }

  return getOverallStatusLabel(item.result.overall);
}

function getQueueStatusClass(item: LoadedItem) {
  if (item.checkState === 'checking') {
    return 'tool-batch-status-processing';
  }

  if (!item.result) {
    return 'tool-batch-status-queued';
  }

  if (item.result.overall === 'pass') {
    return 'tool-batch-status-done';
  }

  if (item.result.overall === 'needs-fixing') {
    return 'tool-compliance-status-warn';
  }

  return 'tool-batch-status-failed';
}

function getCheckStatusClass(status: CheckStatus) {
  if (status === 'pass') {
    return 'tool-compliance-check-pass';
  }
  if (status === 'warn') {
    return 'tool-compliance-check-warn';
  }
  return 'tool-compliance-check-fail';
}

function getFixSuggestion(check: ComplianceCheck) {
  switch (check.id) {
    case 'dimensions':
      return 'Resize or crop to a cleaner listing size before upload.';
    case 'aspect':
      return 'Adjust the crop so the canvas shape fits the platform better.';
    case 'file-size':
      return 'Compress or batch-optimize the file for a lighter upload.';
    case 'format':
      return 'Convert this image to JPG, PNG, or WebP before publishing.';
    case 'transparency':
      return 'Replace transparency with a solid listing background.';
    case 'background':
      return 'Clean the outer background and keep it white or neutral.';
    case 'framing':
      return 'Reframe the product so it has safer space around the edges.';
    default:
      return 'Open the related tool and make a quick cleanup pass.';
  }
}

function distanceFromColor(r: number, g: number, b: number, target: [number, number, number]) {
  const dr = r - target[0];
  const dg = g - target[1];
  const db = b - target[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

async function yieldToBrowser() {
  await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
}

async function loadDrawableFromFile(file: File): Promise<Drawable> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        dispose: () => bitmap.close(),
      };
    } catch {
      // Fall back to HTMLImageElement.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const nextImage = new Image();
    nextImage.onload = () => resolve(nextImage);
    nextImage.onerror = () => reject(new Error('This image could not be decoded in your browser.'));
    nextImage.src = objectUrl;
  });

  return {
    source: image,
    width: image.naturalWidth,
    height: image.naturalHeight,
    dispose: () => URL.revokeObjectURL(objectUrl),
  };
}

async function prepareItem(file: File): Promise<LoadedItem> {
  const drawable = await loadDrawableFromFile(file);

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      width: drawable.width,
      height: drawable.height,
      previewUrl: URL.createObjectURL(file),
      checkState: 'idle',
      result: null,
      error: null,
    };
  } finally {
    drawable.dispose();
  }
}

function analyzeImage(drawable: Drawable): ImageAnalysis {
  const scale = Math.min(1, 220 / Math.max(drawable.width, drawable.height));
  const width = Math.max(24, Math.round(drawable.width * scale));
  const height = Math.max(24, Math.round(drawable.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return {
      transparentRatio: 0,
      borderLuma: 255,
      borderVariance: 0,
      subjectBounds: null,
    };
  }

  context.drawImage(drawable.source, 0, 0, width, height);
  const { data } = context.getImageData(0, 0, width, height);
  const border = Math.max(2, Math.round(Math.min(width, height) * 0.08));
  let borderCount = 0;
  let totalR = 0;
  let totalG = 0;
  let totalB = 0;
  let totalLuma = 0;
  let transparentPixels = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const alpha = data[index + 3]!;

      if (alpha < 250) {
        transparentPixels += 1;
      }

      if (x < border || x >= width - border || y < border || y >= height - border) {
        const r = data[index]!;
        const g = data[index + 1]!;
        const b = data[index + 2]!;
        const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        totalR += r;
        totalG += g;
        totalB += b;
        totalLuma += luma;
        borderCount += 1;
      }
    }
  }

  const avgColor: [number, number, number] = borderCount
    ? [totalR / borderCount, totalG / borderCount, totalB / borderCount]
    : [255, 255, 255];
  const avgLuma = borderCount ? totalLuma / borderCount : 255;

  let varianceAccumulator = 0;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  const transparentBg = transparentPixels / (width * height) > 0.005;
  const subjectThreshold = transparentBg ? 48 : 30;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const alpha = data[index + 3]!;
      const r = data[index]!;
      const g = data[index + 1]!;
      const b = data[index + 2]!;

      if (x < border || x >= width - border || y < border || y >= height - border) {
        varianceAccumulator += Math.abs(0.2126 * r + 0.7152 * g + 0.0722 * b - avgLuma);
      }

      const isForeground = transparentBg
        ? alpha > 48
        : alpha > 220 && distanceFromColor(r, g, b, avgColor) > subjectThreshold;

      if (isForeground) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  return {
    transparentRatio: transparentPixels / (width * height),
    borderLuma: avgLuma,
    borderVariance: borderCount ? varianceAccumulator / borderCount : 0,
    subjectBounds:
      maxX >= minX && maxY >= minY
        ? {
            minX: minX / width,
            minY: minY / height,
            maxX: maxX / width,
            maxY: maxY / height,
          }
        : null,
  };
}

function buildComplianceResult(
  item: LoadedItem,
  platform: PlatformProfile,
  analysis: ImageAnalysis,
) {
  const checks: ComplianceCheck[] = [];
  const longSide = Math.max(item.width, item.height);
  const shortSide = Math.min(item.width, item.height);
  const ratio = item.width / item.height;
  const inputFormat = getInputFormat(item.type);
  const fileSizeMb = item.size / (1024 * 1024);

  const dimensionsStatus: CheckStatus =
    longSide < platform.minLongSide || shortSide < platform.minShortSide
      ? 'fail'
      : longSide < platform.idealLongSide
        ? 'warn'
        : 'pass';
  checks.push({
    id: 'dimensions',
    label: 'Dimensions',
    status: dimensionsStatus,
    detail:
      dimensionsStatus === 'fail'
        ? `Too small for a safe listing starting point at ${item.width} × ${item.height}.`
        : dimensionsStatus === 'warn'
          ? `Usable, but the longest side is below the cleaner ${platform.idealLongSide}px target.`
          : `Strong enough resolution for a practical ${platform.label} listing start.`,
    fixHref: fixToolMap.resize.href,
    fixLabel: fixToolMap.resize.label,
  });

  const aspectStatus: CheckStatus =
    ratio >= platform.passRatioRange[0] && ratio <= platform.passRatioRange[1]
      ? 'pass'
      : ratio >= platform.warnRatioRange[0] && ratio <= platform.warnRatioRange[1]
        ? 'warn'
        : 'fail';
  checks.push({
    id: 'aspect',
    label: 'Aspect ratio',
    status: aspectStatus,
    detail:
      aspectStatus === 'fail'
        ? 'The current frame shape is far from the usual listing shape for this platform.'
        : aspectStatus === 'warn'
          ? 'The image may work, but the frame shape is outside the cleaner default range for this platform.'
          : 'The frame shape is in a practical range for this platform.',
    fixHref: fixToolMap.resize.href,
    fixLabel: fixToolMap.resize.label,
  });

  const sizeStatus: CheckStatus =
    fileSizeMb > platform.maxSuggestedSizeMb * 1.8
      ? 'fail'
      : fileSizeMb > platform.maxSuggestedSizeMb
        ? 'warn'
        : 'pass';
  checks.push({
    id: 'file-size',
    label: 'File weight',
    status: sizeStatus,
    detail:
      sizeStatus === 'fail'
        ? `This file is ${formatFileSize(item.size)}, which is heavy for a practical upload-ready image.`
        : sizeStatus === 'warn'
          ? `This file is ${formatFileSize(item.size)} and may be worth compressing before upload.`
          : `The current file weight looks reasonable for a practical listing upload.`,
    fixHref: fixToolMap.batch.href,
    fixLabel: fixToolMap.batch.label,
  });

  const formatStatus: CheckStatus = platform.preferredFormats.includes(
    inputFormat as 'jpeg' | 'png' | 'webp',
  )
    ? 'pass'
    : inputFormat === 'gif' || inputFormat === 'bmp' || inputFormat === 'unknown'
      ? 'fail'
      : 'warn';
  checks.push({
    id: 'format',
    label: 'Format',
    status: formatStatus,
    detail:
      formatStatus === 'fail'
        ? `${normalizeSourceLabel(item.type)} is not a strong marketplace-ready format here.`
        : formatStatus === 'warn'
          ? `${normalizeSourceLabel(item.type)} can work, but it is not the cleaner first choice for this platform.`
          : `${normalizeSourceLabel(item.type)} is a practical format choice for this platform.`,
    fixHref: fixToolMap.batch.href,
    fixLabel: fixToolMap.batch.label,
  });

  const transparencyStatus: CheckStatus =
    analysis.transparentRatio > 0.005 ? (platform.disallowTransparency ? 'fail' : 'warn') : 'pass';
  checks.push({
    id: 'transparency',
    label: 'Transparency',
    status: transparencyStatus,
    detail:
      transparencyStatus === 'fail'
        ? 'Transparency is likely present, and this platform usually reads better with a solid listing background.'
        : transparencyStatus === 'warn'
          ? 'Transparency may be present. A solid listing background may still feel safer for this platform.'
          : 'No meaningful transparency was detected in the image.',
    fixHref: fixToolMap.background.href,
    fixLabel: fixToolMap.background.label,
  });

  const backgroundStatus: CheckStatus = platform.prefersLightBackground
    ? analysis.borderLuma > 236 && analysis.borderVariance < 18
      ? 'pass'
      : analysis.borderLuma > 214 && analysis.borderVariance < 34
        ? 'warn'
        : 'fail'
    : analysis.borderLuma > 210 && analysis.borderVariance < 38
      ? 'pass'
      : analysis.borderLuma > 180 && analysis.borderVariance < 56
        ? 'warn'
        : 'fail';
  checks.push({
    id: 'background',
    label: 'Background fit',
    status: backgroundStatus,
    detail:
      backgroundStatus === 'fail'
        ? 'The outer background likely looks too dark, busy, or inconsistent for a clean listing presentation.'
        : backgroundStatus === 'warn'
          ? 'The background may work, but it does not look strongly white or neutral yet.'
          : 'The outer background looks light and consistent enough for a cleaner listing presentation.',
    fixHref: fixToolMap.background.href,
    fixLabel: fixToolMap.background.label,
  });

  let framingStatus: CheckStatus = 'warn';
  let framingDetail =
    'The checker could not isolate the subject confidently enough to confirm safe marketplace framing.';

  if (analysis.subjectBounds) {
    const left = analysis.subjectBounds.minX;
    const top = analysis.subjectBounds.minY;
    const right = 1 - analysis.subjectBounds.maxX;
    const bottom = 1 - analysis.subjectBounds.maxY;
    const minMargin = Math.min(left, top, right, bottom);
    const widthCoverage = analysis.subjectBounds.maxX - analysis.subjectBounds.minX;
    const heightCoverage = analysis.subjectBounds.maxY - analysis.subjectBounds.minY;

    framingStatus =
      minMargin >= platform.safeInset * 0.85 && widthCoverage < 0.88 && heightCoverage < 0.88
        ? 'pass'
        : minMargin >= platform.safeInset * 0.45 && widthCoverage < 0.94 && heightCoverage < 0.94
          ? 'warn'
          : 'fail';
    framingDetail =
      framingStatus === 'fail'
        ? 'The subject appears too close to the edges for a safer listing frame.'
        : framingStatus === 'warn'
          ? 'The subject is usable, but it sits a little tight in the canvas for this platform.'
          : 'The subject appears to sit comfortably inside a safer listing frame.';
  }

  checks.push({
    id: 'framing',
    label: 'Safe framing',
    status: framingStatus,
    detail: framingDetail,
    fixHref: fixToolMap.background.href,
    fixLabel: fixToolMap.background.label,
  });

  const failCount = checks.filter((check) => check.status === 'fail').length;
  const warnCount = checks.filter((check) => check.status === 'warn').length;
  const criticalFail = checks.some(
    (check) => (check.id === 'dimensions' || check.id === 'format') && check.status === 'fail',
  );

  const overall: OverallStatus =
    criticalFail || failCount >= 2
      ? 'not-suitable'
      : failCount >= 1 || warnCount >= 1
        ? 'needs-fixing'
        : 'pass';

  const actions: FixAction[] = Array.from(
    new Map(
      checks
        .filter((check) => check.status !== 'pass' && check.fixHref && check.fixLabel)
        .map((check) => {
          const action: FixAction =
            check.fixHref === fixToolMap.resize.href
              ? fixToolMap.resize
              : check.fixHref === fixToolMap.background.href
                ? fixToolMap.background
                : fixToolMap.batch;

          return [action.href, action];
        }),
    ).values(),
  );

  if (!actions.some((action) => action.href === fixToolMap.metadata.href)) {
    actions.push(fixToolMap.metadata);
  }

  return {
    overall,
    title:
      overall === 'pass'
        ? 'Looks ready for a practical listing pass'
        : overall === 'needs-fixing'
          ? 'Usable, but it needs a few fixes first'
          : 'Not a strong listing-ready starting point yet',
    summary:
      overall === 'pass'
        ? 'The image looks strong enough for a practical seller workflow on this platform.'
        : overall === 'needs-fixing'
          ? 'The image can likely work after a quick cleanup pass, reframing, or lighter export.'
          : 'The image is likely to struggle in its current state and should be fixed before upload.',
    checks,
    platformWarning: platform.warning,
    actions,
  };
}

export default function BrowserMarketplaceImageComplianceChecker() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const itemsRef = useRef<LoadedItem[]>([]);
  const [items, setItems] = useState<LoadedItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [platformId, setPlatformId] = useState<PlatformId>('amazon');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const activePlatform =
    platformProfiles.find((platform) => platform.id === platformId) ?? platformProfiles[0];
  const selectedItem = items.find((item) => item.id === selectedItemId) ?? items[0] ?? null;
  const passCount = items.filter((item) => item.result?.overall === 'pass').length;
  const needsFixingCount = items.filter((item) => item.result?.overall === 'needs-fixing').length;
  const notSuitableCount = items.filter((item) => item.result?.overall === 'not-suitable').length;
  const checkedCount = items.filter((item) => item.result).length;
  const failedCount = items.filter((item) => item.error).length;
  const totalQueueSize = items.reduce((sum, item) => sum + item.size, 0);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    return () => {
      itemsRef.current.forEach((item) => {
        URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, []);

  function invalidateResults() {
    setItems((current) =>
      current.map((item) => ({
        ...item,
        checkState: 'idle',
        result: null,
        error: null,
      })),
    );
  }

  async function handleIncomingFiles(fileList: FileList | File[] | null) {
    setErrorMessage(null);

    if (!fileList?.length || isChecking) {
      return;
    }

    const nextFiles = Array.from(fileList);
    const validFiles = nextFiles
      .slice(0, Math.max(0, maxBatchFiles - items.length))
      .filter((file) => supportedTypes.has(file.type) && file.size <= maxFileSize);

    if (!validFiles.length) {
      setErrorMessage(
        `Choose PNG, JPG, JPEG, WebP, GIF, or BMP files up to ${Math.round(maxFileSize / (1024 * 1024))} MB each.`,
      );
      return;
    }

    if (validFiles.length < nextFiles.length) {
      setErrorMessage(
        `Some files were skipped because the checker supports up to ${maxBatchFiles} images and ${Math.round(maxFileSize / (1024 * 1024))} MB per file.`,
      );
    }

    setIsPreparing(true);

    try {
      const prepared: LoadedItem[] = [];

      for (const file of validFiles) {
        prepared.push(await prepareItem(file));
        await yieldToBrowser();
      }

      setItems((current) => [...current, ...prepared]);
      setSelectedItemId((current) => current ?? prepared[0]?.id ?? null);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The images could not be prepared locally.',
      );
    } finally {
      setIsPreparing(false);
    }
  }

  function removeItem(itemId: string) {
    if (isChecking) {
      return;
    }

    setItems((current) => {
      const target = current.find((item) => item.id === itemId);

      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }

      const nextItems = current.filter((item) => item.id !== itemId);

      setSelectedItemId((currentSelected) => {
        if (currentSelected !== itemId) {
          return currentSelected;
        }

        return nextItems[0]?.id ?? null;
      });

      return nextItems;
    });
  }

  function clearAll() {
    if (isChecking) {
      return;
    }

    items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setItems([]);
    setSelectedItemId(null);
    setErrorMessage(null);
  }

  async function runChecks() {
    if (!items.length) {
      setErrorMessage('Upload one or more listing images first.');
      return;
    }

    setIsChecking(true);
    setErrorMessage(null);

    try {
      for (const item of items) {
        setItems((current) =>
          current.map((currentItem) =>
            currentItem.id === item.id
              ? { ...currentItem, checkState: 'checking', error: null, result: null }
              : currentItem,
          ),
        );

        await yieldToBrowser();

        try {
          const drawable = await loadDrawableFromFile(item.file);

          try {
            const analysis = analyzeImage(drawable);
            const result = buildComplianceResult(item, activePlatform, analysis);

            setItems((current) =>
              current.map((currentItem) =>
                currentItem.id === item.id
                  ? {
                      ...currentItem,
                      checkState: 'done',
                      result,
                      error: null,
                    }
                  : currentItem,
              ),
            );
          } finally {
            drawable.dispose();
          }
        } catch (error) {
          setItems((current) =>
            current.map((currentItem) =>
              currentItem.id === item.id
                ? {
                    ...currentItem,
                    checkState: 'done',
                    result: null,
                    error:
                      error instanceof Error
                        ? error.message
                        : 'The local checks could not finish for this file.',
                  }
                : currentItem,
            ),
          );
        }
      }
    } finally {
      setIsChecking(false);
    }
  }

  const selectedChecks = selectedItem?.result?.checks ?? [];
  const selectedOverall = selectedItem?.result?.overall ?? null;
  const selectedIssues = selectedChecks.filter((check) => check.status !== 'pass').slice(0, 4);

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Check whether product images look listing-ready before you upload them</h2>
          <p>
            Upload product images, choose a platform, run local checks, and see what looks ready for
            listing use, what needs fixing, and where to fix it next.
          </p>
        </div>
        <span className="tool-live-badge">Local browser processing</span>
      </div>

      <div className="tool-product-prep-steps" aria-label="Workflow steps">
        <div className="tool-product-prep-step tool-product-prep-step-complete">
          <strong>1. Upload</strong>
          <span>Add product images</span>
        </div>
        <div
          className={`tool-product-prep-step ${items.length ? 'tool-product-prep-step-active' : ''}`}
        >
          <strong>2. Select platform</strong>
          <span>Choose the listing context</span>
        </div>
        <div
          className={`tool-product-prep-step ${
            items.length && !checkedCount ? 'tool-product-prep-step-active' : ''
          } ${checkedCount ? 'tool-product-prep-step-complete' : ''}`}
        >
          <strong>3. Check</strong>
          <span>Run local readiness checks</span>
        </div>
        <div
          className={`tool-product-prep-step ${checkedCount ? 'tool-product-prep-step-active' : ''}`}
        >
          <strong>4. Fix guidance</strong>
          <span>See what to fix next</span>
        </div>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Upload</strong>
              <span>Build a product-image queue that stays on this device.</span>
            </div>
            <button
              className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
              disabled={isChecking}
              onClick={() => inputRef.current?.click()}
              onDragEnter={() => setIsDragActive(true)}
              onDragLeave={() => setIsDragActive(false)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragActive(false);
                void handleIncomingFiles(event.dataTransfer.files);
              }}
              type="button"
            >
              <div>
                <strong>
                  {isPreparing
                    ? 'Preparing images locally…'
                    : items.length
                      ? `${items.length} image${items.length === 1 ? '' : 's'} ready to check`
                      : 'Upload listing images or drop them here'}
                </strong>
                <span>
                  {items.length
                    ? `${items.length} queued • ${formatFileSize(totalQueueSize)} total`
                    : `Up to ${maxBatchFiles} files • ${Math.round(maxFileSize / (1024 * 1024))} MB each • no server upload`}
                </span>
              </div>
            </button>
            <input
              ref={inputRef}
              accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
              className="tool-live-file-input"
              multiple
              onChange={(event) => {
                void handleIncomingFiles(event.target.files);
                event.currentTarget.value = '';
              }}
              type="file"
            />

            {items.length ? (
              <div className="tool-batch-queue-strip" aria-label="Queue status">
                <span>{`${items.length} queued`}</span>
                <span>{`${checkedCount} checked`}</span>
                <span>{`${passCount} pass`}</span>
                <span>{`${needsFixingCount} need fixing`}</span>
                <span>{`${notSuitableCount} not suitable`}</span>
              </div>
            ) : null}

            {items.length ? (
              <div className="tool-batch-list">
                {items.map((item) => (
                  <article
                    className={`tool-batch-item ${
                      selectedItem?.id === item.id ? 'tool-batch-item-active' : ''
                    }`}
                    key={item.id}
                  >
                    <div className="tool-batch-item-top">
                      <strong>{item.name}</strong>
                      <span className={`tool-batch-status ${getQueueStatusClass(item)}`}>
                        {getQueueStatusLabel(item)}
                      </span>
                    </div>
                    <div className="tool-batch-item-meta">
                      <span>{`${normalizeSourceLabel(item.type)} • ${item.width} × ${item.height}`}</span>
                      <span>{formatFileSize(item.size)}</span>
                    </div>
                    <div className="tool-batch-item-actions">
                      <button
                        className={`tool-batch-link ${
                          selectedItem?.id === item.id ? 'tool-batch-link-active' : ''
                        }`}
                        onClick={() => setSelectedItemId(item.id)}
                        type="button"
                      >
                        Review
                      </button>
                      <button
                        className="tool-batch-link"
                        disabled={isChecking}
                        onClick={() => removeItem(item.id)}
                        type="button"
                      >
                        Remove
                      </button>
                      <span className="tool-batch-muted">
                        {item.error
                          ? 'This file could not be checked locally.'
                          : item.result
                            ? item.result.title
                            : 'Ready for local checks'}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Select platform</strong>
              <span>Pick the listing context you want to check against.</span>
            </div>
            <div className="tool-compliance-platform-grid" aria-label="Marketplace presets">
              {platformProfiles.map((platform) => (
                <button
                  key={platform.id}
                  className={`tool-batch-preset-card ${
                    platform.id === platformId ? 'tool-batch-preset-card-active' : ''
                  }`}
                  disabled={isChecking}
                  onClick={() => {
                    setPlatformId(platform.id);
                    invalidateResults();
                  }}
                  type="button"
                >
                  <strong>{platform.label}</strong>
                  <span>{platform.summary}</span>
                </button>
              ))}
            </div>
            <div className="tool-batch-preset-summary">
              <strong>{activePlatform.label}</strong>
              <span>{activePlatform.summary}</span>
            </div>
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Check</strong>
              <span>Run a practical local check. This is not an official validator.</span>
            </div>

            <div className="tool-batch-queue-summary">
              <div className="tool-batch-progress" aria-label="Check progress">
                <div className="tool-batch-progress-bar">
                  <span
                    style={{
                      width: items.length
                        ? `${Math.round((checkedCount / items.length) * 100)}%`
                        : '0%',
                    }}
                  />
                </div>
                <div className="tool-batch-progress-meta">
                  <strong>
                    {items.length ? `${checkedCount} of ${items.length} checked` : 'Queue is empty'}
                  </strong>
                  <span>
                    {items.length
                      ? `${passCount} pass • ${needsFixingCount} need fixing • ${notSuitableCount} not suitable`
                      : 'Upload images first, then run local checks.'}
                  </span>
                </div>
              </div>
              <div className="hero-actions tool-live-actions">
                <button
                  className="button"
                  disabled={!items.length || isPreparing || isChecking}
                  onClick={() => void runChecks()}
                  type="button"
                >
                  {isChecking ? 'Running local checks…' : 'Run local checks'}
                </button>
                <button
                  className="button button-ghost"
                  disabled={!items.length || isChecking}
                  onClick={clearAll}
                  type="button"
                >
                  Clear all
                </button>
              </div>
            </div>
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Fix guidance</strong>
              <span>Review one file, see the problems, and jump into the right fix.</span>
            </div>

            {selectedItem ? (
              <>
                <div className="tool-live-preview tool-compression-preview">
                  <div className="tool-live-preview-frame tool-compliance-preview-frame">
                    <img alt={`${selectedItem.name} preview`} src={selectedItem.previewUrl} />
                  </div>
                </div>

                <div className="tool-compliance-status-card">
                  <div>
                    <span
                      className={`tool-batch-status ${
                        selectedOverall === 'pass'
                          ? 'tool-batch-status-done'
                          : selectedOverall === 'needs-fixing'
                            ? 'tool-compliance-status-warn'
                            : selectedOverall === 'not-suitable'
                              ? 'tool-batch-status-failed'
                              : 'tool-batch-status-queued'
                      }`}
                    >
                      {selectedOverall ? getOverallStatusLabel(selectedOverall) : 'Not checked'}
                    </span>
                    <h3>{selectedItem.result?.title ?? 'Ready to run checks'}</h3>
                    <p>
                      {selectedItem.result?.summary ??
                        'Choose the platform and run local checks to see what needs fixing before upload.'}
                    </p>
                  </div>
                  <div className="tool-batch-preview-applied">
                    <span>{selectedItem.name}</span>
                    <span>{`${normalizeSourceLabel(selectedItem.type)} • ${selectedItem.width} × ${selectedItem.height}`}</span>
                    <span>{formatFileSize(selectedItem.size)}</span>
                    <span>{activePlatform.label}</span>
                  </div>
                </div>

                {selectedIssues.length ? (
                  <div className="tool-compliance-issue-grid">
                    {selectedIssues.map((check) => (
                      <article className="tool-compliance-issue-card" key={check.id}>
                        <div className="tool-compliance-check-top">
                          <strong>{check.label}</strong>
                          <span
                            className={`tool-batch-status ${getCheckStatusClass(check.status)}`}
                          >
                            {check.status === 'warn' ? 'Needs fixing' : 'Not suitable'}
                          </span>
                        </div>
                        <p>{check.detail}</p>
                        <span className="tool-batch-muted">{getFixSuggestion(check)}</span>
                        {check.fixHref && check.fixLabel ? (
                          <Link className="soft-link" href={check.fixHref}>
                            {check.fixLabel}
                          </Link>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : selectedItem.result ? (
                  <div className="tool-batch-stage-note">
                    <strong>No major issues found</strong>
                    <span>
                      This file looks usable for a practical seller workflow on{' '}
                      {activePlatform.label}.
                    </span>
                  </div>
                ) : null}

                {selectedChecks.length ? (
                  <div className="tool-compliance-check-list">
                    {selectedChecks.map((check) => (
                      <article className="tool-compliance-check" key={check.id}>
                        <div className="tool-compliance-check-top">
                          <strong>{check.label}</strong>
                          <span
                            className={`tool-batch-status ${getCheckStatusClass(check.status)}`}
                          >
                            {check.status === 'pass'
                              ? 'Pass'
                              : check.status === 'warn'
                                ? 'Needs fixing'
                                : 'Not suitable'}
                          </span>
                        </div>
                        <p>{check.detail}</p>
                      </article>
                    ))}
                  </div>
                ) : null}

                {selectedItem.result?.actions.length ? (
                  <div className="tool-compliance-actions">
                    {selectedItem.result.actions.map((action) => (
                      <Link className="soft-link" href={action.href} key={action.href}>
                        {action.title}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="tool-editor-empty">
                <strong>Sample result appears here</strong>
                <span>Upload product images, choose a platform, and run the local checker.</span>
              </div>
            )}
          </div>

          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel tool-product-prep-summary">
          <h3>Summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Platform</strong>
              <span>{activePlatform.label}</span>
            </div>
            <div>
              <strong>Queue</strong>
              <span>{items.length ? `${items.length} images` : 'Add images to start'}</span>
            </div>
            <div>
              <strong>Checked</strong>
              <span>{checkedCount ? `${checkedCount} done` : 'Run checks'}</span>
            </div>
            <div>
              <strong>Pass</strong>
              <span>{passCount ? `${passCount} ready` : '—'}</span>
            </div>
            <div>
              <strong>Needs fixing</strong>
              <span>{needsFixingCount ? `${needsFixingCount} flagged` : '—'}</span>
            </div>
            <div>
              <strong>Not suitable</strong>
              <span>{notSuitableCount ? `${notSuitableCount} flagged` : '—'}</span>
            </div>
            <div>
              <strong>Selected</strong>
              <span>{selectedItem ? selectedItem.name : 'Choose one file'}</span>
            </div>
            <div>
              <strong>Selected status</strong>
              <span>{selectedItem ? getQueueStatusLabel(selectedItem) : '—'}</span>
            </div>
            <div>
              <strong>Check errors</strong>
              <span>
                {failedCount ? `${failedCount} file${failedCount === 1 ? '' : 's'}` : '—'}
              </span>
            </div>
          </div>
          <div className="tool-live-panel-note">
            <strong>Local only</strong>
            <span>Upload, analysis, and fix guidance stay in the browser.</span>
          </div>
          <div className="tool-live-panel-note">
            <strong>Practical checker</strong>
            <span>Seller-oriented guidance, not an official marketplace validator.</span>
          </div>
        </aside>
      </div>
    </article>
  );
}
